import 'draft-js/dist/Draft.css';

import Editor from '@draft-js-plugins/editor';
import {
  faAlignLeft,
  faBold,
  faItalic,
  faListOl,
  faListUl,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ContentState,
  convertFromHTML,
  convertToRaw,
  EditorState,
  RichUtils,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import React, { useCallback, useEffect } from 'react';

/**
 * Props for the RichTextEditor component
 */
export interface RichTextEditorProps {
  /** The current editor state */
  editorState: EditorState;
  /** Callback function when editor state changes */
  onChange: (editorState: EditorState) => void;
  /** Placeholder text for the editor */
  placeholder?: string;
  /** Custom class name for the editor container */
  className?: string;
  /** Custom class name for the toolbar */
  toolbarClassName?: string;
  /** Custom class name for the editor content */
  contentClassName?: string;
  /** Whether to show the toolbar */
  showToolbar?: boolean;
  /** Custom block toolbar buttons */
  customBlockToolbarButtons?: ToolbarButton[];
  /** Custom inline toolbar buttons */
  customInlineToolbarButtons?: ToolbarButton[];
  /** HTML content value for controlled input */
  value?: string;
  /** Name of the field for form integration */
  name?: string;
  /** Error message to display */
  error?: string;
  /** Label for the editor */
  label?: string;
  /** Callback for when the editor loses focus */
  onBlur?: () => void;
  /** Whether the editor is read-only */
  readOnly?: boolean;
}

/**
 * Interface for toolbar button configuration
 */
export interface ToolbarButton {
  /** Icon to display */
  icon: any;
  /** Title/tooltip for the button */
  title: string;
  /** Type of button (block or inline) */
  type: 'block' | 'inline';
  /** Style to apply when clicked */
  style: string;
}

/**
 * Default toolbar buttons configuration
 */
const DEFAULT_TOOLBAR_BUTTONS: ToolbarButton[] = [
  {
    icon: faListUl,
    title: 'Bullet list',
    type: 'block',
    style: 'unordered-list-item',
  },
  {
    icon: faListOl,
    title: 'Numbered list',
    type: 'block',
    style: 'ordered-list-item',
  },
  {
    icon: faAlignLeft,
    title: 'Align left',
    type: 'block',
    style: 'align-left',
  },
];

const DEFAULT_INLINE_TOOLBAR_BUTTONS: ToolbarButton[] = [
  {
    icon: faBold,
    title: 'Bold',
    type: 'inline',
    style: 'BOLD',
  },
  {
    icon: faItalic,
    title: 'Italic',
    type: 'inline',
    style: 'ITALIC',
  },
];

/**
 * Converts HTML string to EditorState
 */
const htmlToEditorState = (html: string): EditorState => {
  if (!html) {
    return EditorState.createEmpty();
  }
  const blocksFromHTML = convertFromHTML(html);
  const contentState = ContentState.createFromBlockArray(
    blocksFromHTML.contentBlocks,
    blocksFromHTML.entityMap,
  );
  return EditorState.createWithContent(contentState);
};

/**
 * Converts EditorState to HTML string
 */
const editorStateToHtml = (editorState: EditorState): string => {
  const contentState = editorState.getCurrentContent();
  const rawContentState = convertToRaw(contentState);
  return draftToHtml(rawContentState);
};

/**
 * RichTextEditor component for rich text editing with formatting options
 *
 * @example
 * ```tsx
 * const [editorState, setEditorState] = useState(EditorState.createEmpty());
 *
 * <RichTextEditor
 *   editorState={editorState}
 *   onChange={setEditorState}
 *   placeholder="Enter your text here"
 * />
 * ```
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  editorState,
  onChange,
  placeholder = 'Enter text',
  className = '',
  toolbarClassName = '',
  contentClassName = '',
  showToolbar = true,
  customBlockToolbarButtons,
  customInlineToolbarButtons,
  value,
  error,
  label,
  onBlur,
  readOnly = false,
}) => {
  const blockToolbarButtons =
    customBlockToolbarButtons || DEFAULT_TOOLBAR_BUTTONS;
  const inlineToolbarButtons =
    customInlineToolbarButtons || DEFAULT_INLINE_TOOLBAR_BUTTONS;

  // Handle value prop changes
  useEffect(() => {
    if (value !== undefined) {
      const newEditorState = htmlToEditorState(value);
      onChange(newEditorState);
    }
  }, [value, onChange]);

  /**
   * Handles inline style button clicks
   */
  const handleStyleClick = useCallback(
    (style: string) => {
      onChange(RichUtils.toggleInlineStyle(editorState, style));
    },
    [editorState, onChange],
  );

  /**
   * Handles block type button clicks
   */
  const handleBlockClick = useCallback(
    (blockType: string) => {
      onChange(RichUtils.toggleBlockType(editorState, blockType));
    },
    [editorState, onChange],
  );

  /**
   * Checks if a block type is currently active
   */
  const isBlockActive = useCallback(
    (blockType: string) => {
      const selection = editorState.getSelection();
      const blockKey = selection.getStartKey();
      const currentBlockType = editorState
        .getCurrentContent()
        .getBlockForKey(blockKey)
        .getType();
      return currentBlockType === blockType;
    },
    [editorState],
  );

  /**
   * Checks if an inline style is currently active
   */
  const isInlineStyleActive = useCallback(
    (style: string) => {
      const currentStyle = editorState.getCurrentInlineStyle();
      return currentStyle.has(style);
    },
    [editorState],
  );

  return (
    <div className={`rich-text-editor ${className}`}>
      {label && <div className="rich-text-editor__label">{label}</div>}
      <div className="rich-text-editor__container">
        {showToolbar && (
          <div className={`rich-text-editor__toolbar ${toolbarClassName}`}>
            <div className="rich-text-editor__formatting-buttons">
              {blockToolbarButtons.map((button, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleBlockClick(button.style)}
                  className={`toolbar-button${
                    isBlockActive(button.style) ? ' active' : ''
                  }`}
                  title={button.title}
                  disabled={readOnly}
                >
                  <FontAwesomeIcon icon={button.icon} />
                </button>
              ))}

              {inlineToolbarButtons.map((button, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleStyleClick(button.style)}
                  className={`toolbar-button${
                    isInlineStyleActive(button.style) ? ' active' : ''
                  }`}
                  title={button.title}
                  disabled={readOnly}
                >
                  <FontAwesomeIcon icon={button.icon} />
                </button>
              ))}
            </div>
          </div>
        )}
        <div
          className={`rich-text-editor__content ${contentClassName}${readOnly ? ' read-only' : ''}`}
        >
          <Editor
            editorState={editorState}
            onChange={onChange}
            placeholder={placeholder}
            onBlur={onBlur}
            readOnly={readOnly}
            spellCheck
          />
        </div>
      </div>
      {error && <div className="rich-text-editor__error">{error}</div>}
    </div>
  );
};

export default RichTextEditor;
export { editorStateToHtml, htmlToEditorState };
